import AppContext from 'App/AppContext';
import { IPSeparator, IPSpinner, IPText } from 'baseComponents';
import { IPSpinnerSize } from 'baseComponents/Spinner/IPSpinner';
import { IPStack } from 'baseComponents/Stack/IPStack';
import { ITimelineChart } from 'components/Charts/Timeline/TimelineChart';
import TimelineChartTask, { TimelineTaskGroupMode } from 'components/Charts/Timeline/TimelineChartTask';
import { CopilotButton } from 'components/Copilot/CopilotButtons';
import { CopilotDocument } from 'components/Copilot/Panel/CopilotDocument';
import GenericModal from 'components/Dialogs/GenericModal';
import { Info } from 'components/Notification/Info';
import ScrollableStackItem from 'components/Utils/ScrollableStackItem';
import { globalStackStylesHeight100, globalStackTokensGapMedium, globalStackTokensGapSmall } from 'globalStyles';
import { TFunction } from 'i18next';
import Tag from 'models/tag';
import Task from 'models/tasks/task';
import { TenantOnboarding } from 'models/tenant';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  apiCopilotCreateOnboardingProject,
  apiCopilotCreateOnboardingProjectDocument,
} from 'services/Api/copilotService';
import { apiRequest } from 'services/Auth/authConfig';
import { CopilotAgentEvent, CopilotAgentEventKind, startCopilotHubConnection } from 'services/SignalR/CopilotHub';
import { IHubConnection, stopHubConnection } from 'services/SignalR/GeneralHub';
import { addDateDays } from 'utils/datetime';
import { newGuid } from 'utils/guid';
import { downloadMarkdownDocx } from 'utils/markdownDocx';

interface ITrialWelcomeTimelineProps {
  isOpen: boolean;
  onClose: () => void;
  onboardingState: TenantOnboarding;
  onUpdateProjectDocument: (projectDocument: string | undefined) => void;
}

export const downloadProjectDocument = async (projectDocument: string, t: TFunction<string[]>) => {
  if (!projectDocument) return;

  await downloadMarkdownDocx(projectDocument, t('dashboard:TrialWelcome.Dialogs.Timeline.Filename'));
};

const TrialWelcomeTimeline: React.FunctionComponent<ITrialWelcomeTimelineProps> = (
  props: ITrialWelcomeTimelineProps,
) => {
  const { t } = useTranslation(['translation', 'dashboard']);
  const appContext = useContext(AppContext);
  const timelineRef = useRef<ITimelineChart | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isDocumentCreating, setIsDocumentCreating] = useState<boolean>(false);
  const [projectTasks, setProjectTasks] = useState<Task[]>([]);
  const [projectTags, setProjectTags] = useState<Tag[]>([]);
  const [projectDocument, setProjectDocument] = useState<string | undefined>(undefined);
  const [documentStreamContent, setDocumentStreamContent] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (props.isOpen) {
      setProjectDocument(undefined);
      loadData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.isOpen]);

  const loadData = async () => {
    if (isLoading || !props.isOpen) return;
    try {
      setIsLoading(true);
      setIsDocumentCreating(false);
      setProjectDocument(undefined);
      setProjectTasks([]);

      const newOnboardingSteps = props.onboardingState.onboardingSteps.clone();
      const accessToken: string = await appContext.getAccessToken(apiRequest.scopes);
      const result = await apiCopilotCreateOnboardingProject(
        newOnboardingSteps,
        accessToken,
        appContext.globalDataCache,
      );
      if (result?.tasks) {
        result.tasks.forEach((task) => {
          task.id = newGuid();
        });
        setProjectTasks(result.tasks);
      } else {
        setProjectTasks([]);
      }
      if (result?.tags) {
        setProjectTags(result.tags);
      } else {
        setProjectTags([]);
      }

      onCreateProjectDocument();
    } catch (err) {
      appContext.setError(err);
    } finally {
      setIsLoading(false);
    }
  };

  const onInitialDrawComplete = () => {
    //show the first 14 days of the project in the timeline component
    const start = props.onboardingState.onboardingSteps.startDate ?? new Date();
    timelineRef.current?.setWindow(addDateDays(start, -4), addDateDays(start, 24));
  };

  const onCreateProjectDocument = async () => {
    if (isLoading || !props.isOpen) return;
    let hubConnection: IHubConnection | null = null;
    try {
      setIsDocumentCreating(true);
      setDocumentStreamContent('');
      hubConnection = await startCopilotHubConnection(appContext, (agentEvent: CopilotAgentEvent) => {
        if (agentEvent.kind === CopilotAgentEventKind.TextDelta) {
          setDocumentStreamContent((current) => `${current ?? ''}${agentEvent.text ?? ''}`);
        }
      });
      const accessToken: string = await appContext.getAccessToken(apiRequest.scopes);
      const result = await apiCopilotCreateOnboardingProjectDocument(
        props.onboardingState.onboardingSteps,
        hubConnection?.connectionId || null,
        accessToken,
        appContext.globalDataCache,
      );
      const doc = result?.projectDocument;
      setProjectDocument(doc);
      props.onUpdateProjectDocument(doc);
    } catch (err) {
      appContext.setError(err);
    } finally {
      await stopHubConnection(hubConnection?.connection);
      setIsDocumentCreating(false);
    }
  };

  const showProjectDocument = (): boolean => {
    return isDocumentCreating || projectDocument !== undefined;
  };

  //
  // Main render
  //
  if (!props.isOpen) return null;

  return (
    <GenericModal
      title={t('dashboard:TrialWelcome.Dialogs.Timeline.Title')}
      isOpen={props.isOpen}
      onClose={() => {
        props.onClose();
      }}
      width={'93vw'}
      maxWidth={'93vw'}
      minHeight={650}
      height={'95vh'}
      maxHeight={'95vh'}
      transition={'all 0.6s ease'}
      isBlocking={true}
      hideFooter={true}
    >
      <IPStack verticalFill>
        <IPSeparator />
        <ScrollableStackItem>
          <IPStack horizontal tokens={globalStackTokensGapMedium} styles={globalStackStylesHeight100}>
            <IPStack.Item
              styles={{
                root: { width: '50%', transition: 'all 0.6s ease' },
              }}
            >
              <IPStack verticalFill>
                <IPStack.Item>
                  <IPStack horizontal tokens={globalStackTokensGapMedium}>
                    <IPStack.Item>
                      <IPText>{t('dashboard:TrialWelcome.Dialogs.Timeline.Description')}</IPText>
                    </IPStack.Item>
                    <IPStack.Item>
                      <Info message={t('dashboard:TrialWelcome.Dialogs.Timeline.TimelineInteractionInfo')}></Info>
                    </IPStack.Item>
                  </IPStack>
                </IPStack.Item>
                {isLoading && (
                  <IPStack verticalFill horizontalAlign="center" verticalAlign="center">
                    <IPSpinner size={IPSpinnerSize.large} />
                  </IPStack>
                )}
                {!isLoading && (
                  <IPStack.Item id="timeline-container-goals" grow styles={{ root: { paddingTop: 27 } }}>
                    <TimelineChartTask
                      ref={timelineRef}
                      tasks={projectTasks}
                      tags={projectTags}
                      defaultGroupName={t('dashboard:TrialWelcome.Goals.TimelineDefaultGroup')}
                      groupMode={TimelineTaskGroupMode.Tag}
                      height={`${
                        (document.getElementById('timeline-container-goals')?.clientHeight ?? 300) -
                        (showProjectDocument() ? 35 : 0)
                      }px`}
                      onInitialDrawComplete={onInitialDrawComplete}
                    />
                  </IPStack.Item>
                )}
              </IPStack>
            </IPStack.Item>
            <IPStack.Item
              styles={{
                root: { width: '50%', transition: 'all 0.6s ease' },
              }}
            >
              <IPStack verticalFill tokens={globalStackTokensGapSmall}>
                <IPStack.Item>
                  <CopilotButton
                    text={
                      isDocumentCreating || isLoading
                        ? t('dashboard:TrialWelcome.Dialogs.Timeline.CreateProjectPlan')
                        : t('dashboard:TrialWelcome.Dialogs.Timeline.DownloadProjectPlan')
                    }
                    disabled={isDocumentCreating}
                    showSpinner={isDocumentCreating}
                    onClick={() => {
                      if (projectDocument) {
                        downloadProjectDocument(projectDocument, t);
                      }
                    }}
                    tooltip=""
                  />
                </IPStack.Item>
                <IPStack.Item grow>
                  <CopilotDocument
                    markdown={projectDocument}
                    isStreaming={isDocumentCreating}
                    streamContent={documentStreamContent}
                  />
                </IPStack.Item>
              </IPStack>
            </IPStack.Item>
          </IPStack>
        </ScrollableStackItem>
      </IPStack>
    </GenericModal>
  );
};

export default TrialWelcomeTimeline;
